<script setup>
// T-268 — onde a operação está errando a tabulação.
//
// A cadeia tem até três mãos: o Bira dá o input inicial, o agente altera no encerramento ou na
// transferência, e o backoffice retabula quando o caso chega errado na fila dele. A causa que vale
// é a última; esta tela olha o caminho até ela.
//
// ⚠️ O OBJETIVO É QUALIDADE, NÃO OPERAÇÃO. A pergunta não é "quantos foram retabulados", é "o que
// está sendo tabulado errado, e por quem", para virar treinamento. Por isso o centro da tela são
// os PARES de → para: "ACESSO AO ESTÁDIO virou INGRESSOS oito vezes" diz o que ensinar, e "oito
// retabulações" não diz nada.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import RetabulacaoAPI from 'dashboard/api/fandata/retabulacao';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import AtalhoAnalitico from './components/AtalhoAnalitico.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';

const { t } = useI18n();

const dados = ref(null);
const carregando = ref(true);
const erro = ref(false);
const intervalo = ref([subDays(new Date(), 29), new Date()]);
const tipoDeIntervalo = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const desde = ref(getUnixStartOfDay(subDays(new Date(), 29)));
const ate = ref(getUnixEndOfDay(new Date()));
const canalFiltrado = ref(null);

const carregar = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await RetabulacaoAPI.get({
      since: desde.value,
      until: ate.value,
      inboxId: canalFiltrado.value,
    });
    dados.value = data;
  } catch (e) {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

const aoTrocarPeriodo = valor => {
  const [inicio, fim, tipo] = valor;
  intervalo.value = [inicio, fim];
  tipoDeIntervalo.value = tipo || DATE_RANGE_TYPES.CUSTOM_RANGE;
  desde.value = getUnixStartOfDay(inicio);
  ate.value = getUnixEndOfDay(fim);
  carregar();
};

const temPares = computed(() => (dados.value?.pares || []).length > 0);

const rotuloDoPapel = papel => t(`RETABULACAO.PAPEL.${papel.toUpperCase()}`, papel);

onMounted(carregar);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-start gap-3">
      <WootDatePicker
        v-model:date-range="intervalo"
        v-model:range-type="tipoDeIntervalo"
        @date-range-changed="aoTrocarPeriodo"
      />
      <FiltrosRelatorio
        v-model:inbox-id="canalFiltrado"
        :com-lado="false"
        @update:inbox-id="carregar"
      />
      <BaixarRelatorio
        nome="retabulacao"
        :dados="dados"
        :since="desde"
        :until="ate"
      />
      <!-- T-260 (leitura A): o caminho para a quebra completa, que já existe numa tela só. -->
      <AtalhoAnalitico />
    </div>

    <p class="text-sm text-n-slate-11">{{ t('RETABULACAO.COMO_LER') }}</p>

    <!-- A medição é nova, e a tela diz isso. Mostrar um passado vazio como "ninguém retabulou"
         seria pior do que admitir que a cadeia só passou a ser gravada agora. -->
    <p class="text-xs text-n-amber-11">{{ t('RETABULACAO.DESDE_QUANDO') }}</p>

    <div v-if="carregando" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('RETABULACAO.CARREGANDO') }}
    </div>
    <div v-else-if="erro" class="py-8 text-sm text-center text-n-ruby-11">
      {{ t('RETABULACAO.ERRO') }}
    </div>

    <template v-else-if="dados">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('RETABULACAO.TABULADAS') }}
          </span>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ dados.tabuladas }}
          </p>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('RETABULACAO.RETABULADAS') }}
          </span>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-amber-11">
            {{ dados.retabuladas }}
          </p>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('RETABULACAO.TAXA') }}
          </span>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ dados.taxa === null ? '–' : `${dados.taxa}%` }}
          </p>
        </div>
      </div>

      <div v-if="!temPares" class="py-10 text-sm text-center text-n-slate-11">
        {{ t('RETABULACAO.VAZIO') }}
      </div>

      <template v-else>
        <div class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
          <div class="px-5 py-3 border-b border-n-weak">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('RETABULACAO.PARES') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ t('RETABULACAO.PARES_HINT') }}
            </p>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-n-slate-11">
                <th class="px-5 py-2 font-medium text-left">
                  {{ t('RETABULACAO.COL_DE') }}
                </th>
                <th class="px-5 py-2 font-medium text-left">
                  {{ t('RETABULACAO.COL_PARA') }}
                </th>
                <th class="px-3 py-2 font-medium text-center">
                  {{ t('RETABULACAO.COL_VEZES') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="par in dados.pares"
                :key="`${par.de}-${par.para}`"
                class="border-t border-n-weak"
              >
                <td class="px-5 py-2 capitalize text-n-slate-11">{{ par.de }}</td>
                <td class="px-5 py-2 font-medium capitalize text-n-slate-12">
                  {{ par.para }}
                </td>
                <td class="px-3 py-2 font-semibold text-center tabular-nums">
                  {{ par.vezes }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
            <div class="px-5 py-3 border-b border-n-weak">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ t('RETABULACAO.POR_PAPEL') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ t('RETABULACAO.POR_PAPEL_HINT') }}
              </p>
            </div>
            <div
              v-for="linha in dados.por_papel"
              :key="linha.papel"
              class="flex items-center justify-between px-5 py-2 text-sm border-t border-n-weak"
            >
              <span class="text-n-slate-12">{{ rotuloDoPapel(linha.papel) }}</span>
              <span class="font-semibold tabular-nums">{{ linha.vezes }}</span>
            </div>
          </div>

          <div class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
            <div class="px-5 py-3 border-b border-n-weak">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ t('RETABULACAO.MAIS_CORRIGIDAS') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ t('RETABULACAO.MAIS_CORRIGIDAS_HINT') }}
              </p>
            </div>
            <div
              v-for="linha in dados.mais_corrigidas"
              :key="linha.causa"
              class="flex items-center justify-between gap-3 px-5 py-2 text-sm border-t border-n-weak"
            >
              <span class="capitalize truncate text-n-slate-12">{{ linha.causa }}</span>
              <span class="font-semibold tabular-nums">{{ linha.vezes }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
